/**
 * Sentry — configuré pour dire QUE ça a cassé et OÙ, jamais AVEC QUELLES DONNÉES.
 *
 * Cette application manipule des CV, des profils LinkedIn et des adresses email. Le risque n'est
 * pas théorique : un rapport d'erreur trop bavard exfiltre chez un tiers exactement ce que les
 * utilisateurs nous confient de plus personnel.
 *
 * LA RÈGLE EST UNE LISTE BLANCHE, PAS UNE LISTE NOIRE. `beforeSend` ne retire pas des champs d'un
 * événement : il en RECONSTRUIT un à partir des seuls champs autorisés. La différence compte. Une
 * liste noire laisse passer tout ce qu'on n'a pas pensé à nommer — y compris les champs qu'une
 * future version du SDK ajoutera. Une liste blanche laisse tomber l'inconnu par défaut.
 *
 * `sendDefaultPii` est déjà à `false` par défaut dans le SDK ; on l'écrit quand même, parce qu'un
 * défaut tacite n'est pas une décision et se renverse sans bruit d'une version à l'autre.
 */
import * as Sentry from '@sentry/react-native';
import * as Updates from 'expo-updates';
import { Platform } from 'react-native';
import * as Application from 'expo-application';
import { canalDeLivraison } from '../config/canal';

/** Contextes de capture : l'écran, au sens « d'où vient l'incident ». */
export type SentryScope =
  | 'signup'
  | 'signin'
  | 'oauth'
  | 'signout'
  | 'confirm'
  | 'reset'
  | 'delete_account'
  // Erreur de CONFIGURATION du build, découverte au lancement : personne parmi les utilisateurs
  // n'y peut rien, et c'est exactement la famille 2.
  | 'config'
  // Paiement du coaching : refus du prestataire, exception, ou retour illisible.
  | 'paiement';

// Seuls ces tags sortent. Tout autre tag posé ailleurs (par le SDK ou par du code futur) est
// abandonné — c'est le but de la liste blanche.
const TAGS_AUTORISES = [
  'auth_code',
  'auth_scope',
  'platform',
  'app_version',
  'update_id',
  'capture',
  'launch_id',
] as const;

/**
 * INSTRUMENT DE MESURE, PAS CORRECTIF — posé le 20/09/2026 pour trancher une question ouverte.
 *
 * Sentry montre des émissions groupées par deux ou trois à deux secondes d'intervalle, ce qui
 * suggère plusieurs exécutions par lancement. Or la lecture du code dit l'inverse :
 * `verifierConfigurationStripe` a UN appelant, en portée de module, `App.tsx` a UN importateur,
 * et il n'existe ni `StrictMode`, ni `reloadAsync`, ni second enregistrement de racine. Le bundle
 * publié le confirme — une seule occurrence de la fonction et de son code d'incident.
 *
 * Les deux lectures restent donc possibles : plusieurs lancements rapprochés (un redémarrage crée
 * un contexte JS neuf, donc ré-évalue le module), ou quelque chose qu'on ne voit pas d'ici.
 *
 * Cette valeur est tirée UNE FOIS par contexte JavaScript. Deux incidents qui la partagent
 * viennent du même lancement — et prouvent alors la double exécution ; deux valeurs distinctes
 * prouvent deux lancements. La question se règle à la première remontée, sans supposer.
 */
const LAUNCH_ID = Math.random().toString(36).slice(2, 10);

// Le contexte `ota_updates` est posé automatiquement par `expoContextIntegration`. On n'en garde
// que ce qui identifie le CODE qui tourne : rien là-dedans ne décrit une personne.
const OTA_AUTORISE = ['update_id', 'channel', 'runtime_version', 'is_embedded_launch', 'is_emergency_launch'] as const;

const MAX_VALEUR = 200;

/**
 * Marque les événements que CE DÉPÔT a délibérément émis, par opposition aux exceptions attrapées
 * par les gestionnaires globaux.
 *
 * La distinction décide du sort du texte. Nos messages sont écrits en dur dans ce dépôt : on sait
 * ce qu'ils contiennent, ils peuvent sortir. Le message d'une exception quelconque, non — il peut
 * citer ce qu'il était en train de traiter, c'est-à-dire un CV.
 */
const TAG_DELIBERE = 'capture';
const VALEUR_ABANDONNEE = '[message non émis par l’app — retiré]';

/**
 * Retire d'un texte QU'ON A ÉCRIT ce qui pourrait malgré tout porter une donnée.
 *
 * Ne s'applique qu'aux messages dont ce dépôt est l'auteur (cf. `TAG_DELIBERE`). Pour un texte
 * d'origine inconnue, rédiger ne suffit pas : on ne peut pas énumérer les formes qu'une donnée
 * personnelle peut prendre. Celui-là n'est pas nettoyé, il est abandonné.
 */
export function redigerValeur(valeur: string | undefined): string | undefined {
  if (!valeur) return valeur;
  return valeur
    .replace(/[\w.+-]+@[\w-]+\.[\w.]+/g, '[email]')
    // Chaîne longue sans espace : jeton, JWT, clé, identifiant de stockage.
    .replace(/\b[A-Za-z0-9_-]{32,}\b/g, '[token]')
    // Une URL garde son origine et son chemin ; la query porte souvent l'identité.
    .replace(/(https?:\/\/[^\s?#]+)[?#][^\s]*/g, '$1')
    .slice(0, MAX_VALEUR);
}

type Scalaire = string | number | boolean | null | undefined;

/**
 * Ne conserve que les clés nommées, ET seulement si leur valeur est scalaire. Un objet imbriqué
 * inattendu (que le SDK pourrait introduire demain sous une clé autorisée) est abandonné plutôt
 * qu'envoyé sans avoir été lu par personne.
 */
function filtrerObjet<T extends string>(
  source: Record<string, unknown> | undefined,
  autorises: readonly T[],
): Record<string, Scalaire> | undefined {
  if (!source) return undefined;
  const sortie: Record<string, Scalaire> = {};
  for (const cle of autorises) {
    const valeur = source[cle];
    if (valeur === undefined || valeur === null) continue;
    if (typeof valeur === 'string' || typeof valeur === 'number' || typeof valeur === 'boolean') {
      sortie[cle] = valeur;
    }
  }
  return Object.keys(sortie).length > 0 ? sortie : undefined;
}

/**
 * Ne survivent que les fils d'Ariane de NAVIGATION, réduits aux noms d'écrans.
 *
 * Les autres catégories sont précisément les dangereuses : `console` emporterait nos propres
 * `console.warn('[auth]', …)` — donc le message brut de Supabase que ce projet a banni de
 * l'affichage — et `http`/`xhr`/`fetch` emporteraient les URL appelées, query comprise.
 * Un nom d'écran, lui, est le « OÙ » recherché et ne décrit personne.
 */
export function filtrerBreadcrumb(breadcrumb: Sentry.Breadcrumb): Sentry.Breadcrumb | null {
  if (breadcrumb.category !== 'navigation') return null;
  return {
    category: 'navigation',
    type: breadcrumb.type,
    timestamp: breadcrumb.timestamp,
    data: filtrerObjet(breadcrumb.data, ['from', 'to']),
  };
}

/**
 * Reconstruit un événement à partir des seuls champs autorisés.
 *
 * LE TEXTE D'UNE EXCEPTION ÉTRANGÈRE EST ABANDONNÉ, pas nettoyé. On perd « undefined is not a
 * function », qui était inoffensif ; on évite « parse failed: <le CV de Marie> », qui ne l'est pas,
 * et qu'aucune expression régulière ne sait distinguer du premier. Le coût de regroupement est
 * faible : quand une pile est présente, c'est elle que Sentry utilise pour regrouper, pas la valeur.
 */
export function assainirEvenement(event: Sentry.ErrorEvent): Sentry.ErrorEvent {
  const delibere = event.tags?.[TAG_DELIBERE] === 'deliberate';

  const exception = event.exception?.values && {
    values: event.exception.values.map((v) => ({
      type: v.type,
      value: delibere ? redigerValeur(v.value) : VALEUR_ABANDONNEE,
      mechanism: v.mechanism,
      stacktrace: v.stacktrace && {
        ...v.stacktrace,
        // `vars` porte les variables locales de la frame — donc, potentiellement, le CV lui-même.
        frames: v.stacktrace.frames?.map(({ vars: _vars, ...frame }) => frame),
      },
    })),
  };

  return {
    // Discriminant du type `ErrorEvent` : `undefined` signifie « événement d'erreur », par
    // opposition à une transaction.
    type: undefined,
    event_id: event.event_id,
    timestamp: event.timestamp,
    platform: event.platform,
    level: event.level,
    environment: event.environment,
    release: event.release,
    dist: event.dist,
    sdk: event.sdk,
    // `message` n'est conservé que parce que nos captures délibérées en posent un, et qu'il est
    // écrit en dur dans ce dépôt. Il passe quand même par la rédaction.
    message: delibere ? redigerValeur(event.message) : undefined,
    exception,
    tags: filtrerObjet(event.tags, TAGS_AUTORISES),
    // SANS CETTE LIGNE, L'EMPREINTE SERAIT POSÉE PUIS JETÉE. La liste blanche ne filtre pas des
    // champs, elle en reconstruit un depuis rien : tout ce qui n'y figure pas disparaît, y compris
    // ce que ce module vient lui-même de poser. Le regroupement serait silencieusement retombé sur
    // la pile, c'est-à-dire sur le défaut qu'on corrige.
    fingerprint: event.fingerprint,
    contexts: { ota_updates: filtrerObjet(event.contexts?.ota_updates, OTA_AUTORISE) },
    breadcrumbs: event.breadcrumbs?.map(filtrerBreadcrumb).filter((b): b is Sentry.Breadcrumb => b !== null),
    // Explicitement jamais : identité, requête réseau, extras libres, pièces jointes.
    user: undefined,
    request: undefined,
    extra: undefined,
    server_name: undefined,
  };
}

/**
 * ENVIRONNEMENT SENTRY — DÉCLARÉ, JAMAIS DÉDUIT DE `__DEV__`.
 *
 * CE QU'ON RÉPARE. La ligne d'avant était `__DEV__ ? 'development' : 'production'`. `__DEV__` ne
 * dit pas « ceci est la production » : il dit « ceci n'est pas un bundle de développement ». Tout
 * export non-dev tombait donc dans `production`, y compris un `expo export --platform web` lancé
 * sur un poste. Mesuré le 18/09/2026 : un export web servi en local a émis dans le projet Sentry de
 * PRODUCTION au premier chargement de page. Le défaut n'est pas propre au web — il vaut pour tout
 * build non-dev qui n'est pas la production, `preview` compris.
 *
 * D'OÙ ÇA VIENT MAINTENANT, dans l'ordre :
 *  1. `EXPO_PUBLIC_SENTRY_ENV` — la déclaration explicite. C'est elle qu'un déploiement web doit
 *     poser (`production`), puisque rien d'autre ne peut le savoir à sa place.
 *  2. `Updates.channel` — posé par EAS depuis `eas.json`, donc `development`, `preview` ou
 *     `production`. Sur un build EAS, c'est la vérité, et elle n'est pas devinée : elle est écrite
 *     dans le profil de build.
 *  3. `local` — ni l'un ni l'autre : on n'est pas passé par EAS. Volontairement DIFFÉRENT de
 *     `development`, qui est un vrai canal EAS : un build dev-client et un `expo start` sur un
 *     poste ne sont pas le même contexte, et les confondre rendrait le filtre inutile.
 *
 * LE SUFFIXE SÉPARE LE WEB DU MOBILE, ce qui est le but : `production-web` et `production-mobile`
 * se filtrent d'un clic dans Sentry. iOS et Android restent ensemble sous `mobile` — le tag
 * `platform`, posé plus bas, les distingue déjà sans fragmenter les environnements.
 *
 * ⚠️ CONSÉQUENCE OPÉRATIONNELLE : l'environnement mobile passe de `production` à
 * `production-mobile`. Toute alerte ou vue Sentry filtrée sur `environment:production` doit être
 * mise à jour, sans quoi elle cessera silencieusement de remonter quoi que ce soit.
 */
export function environnementSentry(): string {
  return `${canalDeLivraison()}-${Platform.OS === 'web' ? 'web' : 'mobile'}`;
}

let actif = false;

/** Sentry est-il réellement branché ? Faux sans DSN — les captures deviennent alors inertes. */
export function sentryActif(): boolean {
  return actif;
}

/**
 * Branche Sentry si un DSN est fourni. Son absence n'est PAS une erreur : en test et en
 * développement local il n'y en a pas, et l'application doit démarrer sans.
 */
export function initSentry(): void {
  // EXPO_PUBLIC_OPTIONNEL: absence NORMALE en développement et en test (aucun DSN local), et une
  // ERREUR en production. Le repli n'est donc pas une valeur, c'est un comportement : on n'initialise
  // pas, et on le dit fort si le build est un vrai build. Un simple `?? ''` aurait reproduit
  // exactement la panne que ce garde-fou documente — une fonctionnalité éteinte que personne ne voit.
  const dsn = process.env.EXPO_PUBLIC_SENTRY_DSN;
  if (!dsn) {
    if (!__DEV__) {
      console.error(
        '[sentry] EXPO_PUBLIC_SENTRY_DSN absent dans un build de production : aucun incident ne sera remonté.',
      );
    }
    return;
  }

  /**
   * CANAL `local` = ON NE REMONTE RIEN. Un poste de développement n'a rien à envoyer au Sentry de
   * production : il consomme le quota, et il noie les vrais incidents sous les erreurs qu'on est
   * en train de provoquer soi-même. Mesuré le 19/09/2026 : 63 des 66 `stripe_test_key_in_production`
   * venaient d'exports web locaux, contre 3 réels.
   *
   * `local` signifie précisément « on n'est passé ni par un canal EAS ni par une déclaration
   * explicite ». C'est donc aussi le cas du WEB DÉPLOYÉ tant qu'il ne se nomme pas — d'où le
   * garde-fou ci-dessous, qui distingue les deux par la seule chose qui les sépare : un poste de
   * développement tourne en `__DEV__`, un déploiement non.
   */
  if (canalDeLivraison() === 'local') {
    if (!__DEV__) {
      console.error(
        '[sentry] Build exporté sans canal : EXPO_PUBLIC_SENTRY_ENV doit être défini AU MOMENT DE ' +
          'L’EXPORT (la variable est inlinée dans le bundle, la poser à l’exécution n’a aucun effet). ' +
          'Aucun incident ne sera remonté.',
      );
    }
    return;
  }

  Sentry.init({
    dsn,
    // Aucune donnée personnelle déduite automatiquement (IP, identité de session).
    sendDefaultPii: false,
    // Les trois pièces jointes qui emporteraient le contenu de l'écran. Un écran de l'éditeur de
    // CV capturé en image, c'est le CV entier parti chez un tiers.
    attachScreenshot: false,
    attachViewHierarchy: false,
    attachStacktrace: true,
    // Les URL appelées n'ont pas à sortir, même en cas d'échec.
    enableCaptureFailedRequests: false,
    environment: environnementSentry(),
    integrations: (defauts) =>
      // Le nom de l'appareil est une donnée personnelle — « iPhone de Marie » — et n'apprend rien
      // sur la panne.
      defauts.filter((integration) => integration.name !== 'DeviceContext'),
    beforeBreadcrumb: filtrerBreadcrumb,
    beforeSend: assainirEvenement,
  });

  // L'update ID est déjà posé en CONTEXTE par `expoContextIntegration`, mais un contexte ne se
  // filtre pas : on ne peut ni chercher ni regrouper les incidents par update. Recopié en TAG, il
  // répond à la seule question qui compte quand quelque chose casse — quel correctif l'a cassé.
  Sentry.setTag('update_id', Updates.updateId ?? 'embedded');
  Sentry.setTag('platform', Platform.OS);
  Sentry.setTag('launch_id', LAUNCH_ID);
  // Lu directement depuis `expo-application`, et non via `services/appVersion` : celui-ci importe
  // le client Supabase, qui se retrouverait alors dans la chaîne d'import de `authErrors` — le
  // module le plus feuille du chemin d'erreur. Une ligne dupliquée vaut mieux qu'un cycle.
  //
  // SUR LE WEB, `nativeApplicationVersion` rend TOUJOURS `null` : il n'y a pas de binaire, donc
  // pas de `CFBundleShortVersionString`. Le tag valait donc « inconnue » sur toute la plateforme.
  // EXPO_PUBLIC_OPTIONNEL: le repli vient de là où la version existe encore, la configuration du
  // build. `Updates.runtimeVersion` a été écarté : il est toujours disponible et toujours exact,
  // mais il désigne l'EMPREINTE NATIVE, pas la version publiée — un chiffre juste répondant à une
  // autre question est pire qu'un trou, parce qu'on ne se méfie pas d'un champ rempli.
  Sentry.setTag(
    'app_version',
    Application.nativeApplicationVersion ?? process.env.EXPO_PUBLIC_APP_VERSION ?? 'inconnue',
  );

  actif = true;
}

/**
 * Signale un incident dont l'utilisateur n'est pas responsable et qu'il ne peut pas corriger.
 *
 * Le message est écrit ICI, en dur : il ne contient jamais la chaîne du serveur, seulement le code
 * et l'endroit. C'est aussi ce qui rend le regroupement lisible — un incident par code et par
 * écran, et non un par utilisateur.
 */
export function empreinte(code: string, scope: SentryScope): string[] {
  return ['incident', scope, code];
}

export function capturerIncident(code: string, scope: SentryScope): void {
  if (!actif) return;
  Sentry.captureMessage(`auth: ${code} (${scope})`, {
    level: 'error',
    tags: { auth_code: code, auth_scope: scope, [TAG_DELIBERE]: 'deliberate' },
    // L'IDENTITÉ DU DÉFAUT EST LE CODE ET LA PORTÉE, PAS LA PILE.
    //
    // Sentry regroupe par défaut sur la pile d'appel. Or une capture DÉLIBÉRÉE emporte la pile de
    // ce qui tournait à cet instant, qui n'a rien à voir avec le défaut : le 20/09/2026,
    // `stripe_test_key_in_production` s'est ainsi réparti sur quatre incidents distincts, rangés
    // sous `Object.l`, `capturerIncident` et — le plus trompeur — `CoachingScreen`, écran sans
    // aucun rapport avec une clé mal configurée. Un même défaut en quatre entrées, c'est quatre
    // fois moins de chances qu'un seuil d'alerte se déclenche, et un décompte faux.
    //
    // `fingerprint` appartient à `ScopeContext` et se passe donc directement à `captureMessage`
    // (vérifié dans les types du SDK : `CaptureContext = Scope | Partial<ScopeContext> | …`).
    fingerprint: empreinte(code, scope),
  });
}
